/**
 * Scope-key + duplicate detection for daily caps (DNKADM-0057 U1).
 *
 * A daily cap is unique on `(scope, membershipPlanId, creditBucketId)` server-side
 * (the create/update `409` enforces it). The UI **warns** on a client-side duplicate
 * (a configuration smell — the most-specific-wins rule still resolves it
 * deterministically) and ALSO surfaces the server `409`; it never blocks submit on
 * the client warning alone.
 *
 * The scope is implied by which ids are set (`deriveScope` in `@/api/dailyCaps`), so
 * a stable key over `(planId, bucketId)` — with `null` ("all") as its own distinct
 * scope — captures the uniqueness exactly: an all-plans cap and a plan-X cap at the
 * same bucket do NOT collide (different scopes).
 */
import type { DailyCap } from '@/api/dailyCaps';

/**
 * Stable key for a cap's `(plan, bucket)` scope. A `null` axis is tagged `*` (its own
 * distinct "all" scope), a set id is tagged with a `P:`/`B:` prefix so a plan/bucket
 * id literally equal to `*` cannot collide with the all scope. The two axes are joined
 * with a `#` separator that cannot appear in an id.
 */
export function capKey(
  membershipPlanId: string | null,
  creditBucketId: string | null,
): string {
  const plan = membershipPlanId === null ? '*' : `P:${membershipPlanId}`;
  const bucket = creditBucketId === null ? '*' : `B:${creditBucketId}`;
  return `${plan}#${bucket}`;
}

/**
 * The set of `(plan, bucket)` keys that appear on MORE than one cap — i.e. every
 * duplicated scope. Used by the list to flag each colliding row.
 */
export function duplicateKeys(caps: readonly DailyCap[]): Set<string> {
  const counts = new Map<string, number>();
  for (const c of caps) {
    const key = capKey(c.membershipPlanId, c.creditBucketId);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const dupes = new Set<string>();
  for (const [key, count] of counts) {
    if (count > 1) dupes.add(key);
  }
  return dupes;
}

/**
 * True when a cap with `(plan, bucket)` would collide with an existing cap OTHER than
 * `excludeId` (the cap being edited). Used by the dialog for the inline duplicate
 * warning — it warns, it does not block submit (the server's unique `409` then
 * surfaces inline on save).
 */
export function collidesWithExisting(
  caps: readonly DailyCap[],
  membershipPlanId: string | null,
  creditBucketId: string | null,
  excludeId: string | null,
): boolean {
  const key = capKey(membershipPlanId, creditBucketId);
  return caps.some(
    (c) =>
      (excludeId === null || c.id !== excludeId) &&
      capKey(c.membershipPlanId, c.creditBucketId) === key,
  );
}
